import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProcessingProvider from "./contexts/ProcessingContext";
import { ChapterProvider } from "./contexts/ChapterContext";
import MainLayout from "./components/MainLayout";
import OnboardingFlow from "./components/OnboardingFlow";
import Onboarding from "./components/features/onboarding/Onboarding";
import ProfilePage from "./components/ProfilePage";
import ReelsPage from "./components/ReelsPage";
import Phase2Demo from "./components/Phase2Demo";
import CarouselTestPage from "./components/CarouselTestPage";
import AuthModal from "./components/auth/AuthModal";
import ResetPasswordScreen from "./components/auth/ResetPasswordScreen";
import AuthCallbackHandler from "./components/auth/AuthCallbackHandler";
import PrivacyPage from "./components/PrivacyPage";
import TermsPage from "./components/TermsPage";
import { OfflineBanner } from "./components/ui/OfflineBanner";
import injectDevUser from "./lib/auth/injectDevUser";
import progressTracker from "./services/progressTracker";
import {
  getToken as getStoredToken,
  getAuthHeader,
} from "./services/authToken";
import "./App.css";
import CookieConsent from "./components/CookieConsent";
import api from "./services/api";
import scrolledLogoWide from "./assets/scrolled-logo-wide.png";
import { useTranslation } from "react-i18next";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Environment detection for authentication mode
const isProduction =
  process.env.NODE_ENV === "production" ||
  process.env.NODE_ENV === "prod" ||
  process.env.REACT_APP_ENV === "production" ||
  window.location.hostname.includes("scrolled.app");

// Enable onboarding in production or when explicitly set
const onboardingFlag = (
  process.env.REACT_APP_ENABLE_ONBOARDING || ""
).toLowerCase();
const ENABLE_ONBOARDING = onboardingFlag !== "false";

// 🆕 Story 1.5.9: Removed old shouldShowOnboarding helper - now centralized in AuthContext

// Main App Component (wrapped with AuthProvider)
function AppContent() {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, loading, shouldShowOnboarding } = useAuth(); // 🆕 Story 1.5.9: Use centralized gating

  // RTL Logic
  useEffect(() => {
    const isRTL = ['ar', 'fa'].includes(i18n.language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const routerBasename = useMemo(() => {
    const rawPublicUrl = process.env.PUBLIC_URL;
    if (!rawPublicUrl) {
      return undefined;
    }

    try {
      const normalizedPath = new URL(
        rawPublicUrl,
        window.location.origin
      ).pathname.replace(/\/+$/, "");
      return normalizedPath && normalizedPath !== "/"
        ? normalizedPath
        : undefined;
    } catch (error) {
      if (rawPublicUrl.startsWith("/")) {
        const cleaned = rawPublicUrl.replace(/\/+$/, "");
        return cleaned && cleaned !== "/" ? cleaned : undefined;
      }
      return undefined;
    }
  }, []);

  console.log("🔍 App Environment Detection:", {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    hostname: window.location.hostname,
    isProduction,
    ENABLE_ONBOARDING,
    isAuthenticated,
    user: user?.email,
  });

  // 🆕 Story 1.5.9: Simplified initialization - onboarding gating now centralized in AuthContext
  useEffect(() => {
    // 🔧 DEV ONLY: Inject dev user authentication if needed (only in non-production)
    const initializeDevAuth = async () => {
      if (!isProduction) {
        await injectDevUser();
      }
    };

    // Initialize dev auth first (non-blocking) only in development
    if (!isProduction) {
      initializeDevAuth();
    }

    // In production mode, show auth modal if not authenticated
    if (isProduction && !loading && !isAuthenticated) {
      console.log(
        "🔒 Production mode: User not authenticated, showing auth modal"
      );
      setShowAuthModal(true);
      return;
    }

    // 🚀 FEATURE FLAG: Skip onboarding if disabled
    if (!ENABLE_ONBOARDING && isAuthenticated && user) {
      console.log(
        "🚀 Onboarding disabled via feature flag - proceeding directly to main app"
      );

      // Initialize progress tracker when user is authenticated
      const token = getStoredToken();
      if (token && user.id) {
        console.log("📖 Initializing progress tracker for user:", user.id);
        progressTracker.initialize(user.id, token);
      }

      // Automatically set default instance
      const defaultInstance = {
        id: "11111111-1111-1111-1111-111111111111",
        name: "The Adventures of Arka",
        description: "Optimized test story for ScrollEd processing",
      };

      setSelectedInstance(defaultInstance);
      return; // Skip normal onboarding check
    }

    // Initialize progress tracker for authenticated users who completed onboarding
    if (isAuthenticated && user && !shouldShowOnboarding) {
      const token = getStoredToken();
      if (token && user.id) {
        console.log("📖 Initializing progress tracker for user:", user.id);
        progressTracker.initialize(user.id, token);
      }

      // Set default instance if not already set
      if (!selectedInstance) {
        setSelectedInstance({
          id: "11111111-1111-1111-1111-111111111111",
          name: "The Adventures of Arka",
          description: "Optimized test story for ScrollEd processing",
        });
      }
    }
  }, [loading, isAuthenticated, user, shouldShowOnboarding, selectedInstance]); // Re-check when auth state changes

  const [currentUser] = useState({
    id: "current_user",
    name: "You",
    handle: "@you",
    avatar_url:
      "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face",
  });

  const handleOnboardingComplete = (data) => {
    console.log("🎉 Onboarding completed:", data);

    // Initialize progress tracker when onboarding completes
    if (user && isAuthenticated) {
      const token = getStoredToken();
      if (token && user.id) {
        console.log(
          "📖 Initializing progress tracker after onboarding:",
          user.id
        );
        progressTracker.initialize(user.id, token);
      }
    }

    // Save selected instance (onboarding_completed flag now managed by AuthContext)
    localStorage.setItem(
      "scrolled_selected_instance",
      JSON.stringify(data.selectedInstance)
    );
    try {
      localStorage.removeItem("show_onboarding_after_verification");
    } catch {}

    setSelectedInstance(data.selectedInstance);
    // Note: onboarding completion is now tracked in user.onboarding_completed via AuthContext
  };

  const resetOnboarding = () => {
    localStorage.removeItem("scrolled_onboarding_completed");
    localStorage.removeItem("scrolled_selected_instance");
    setSelectedInstance(null);
    // Note: Actual reset requires updating user.onboarding_completed via API
  };

  const FallbackRoute = () => {
    const href = window.location.href;
    if (href.includes("/auth-callback#") || href.includes("/auth-callback?")) {
      return <AuthCallbackHandler />;
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Page not found
          </h2>
          <p className="text-gray-600">
            The link you followed is no longer valid.
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg shadow hover:from-purple-700 hover:to-blue-700 transition"
          >
            Back to ScrollEd
          </button>
        </div>
      </div>
    );
  };

  // Show loading screen while auth is initializing
  // Brand-consistent loading screen with wide logo
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#0B0B13' }}>
        <div className="text-center">
          <div className="animate-pulse">
            <img
              src={scrolledLogoWide}
              alt="ScrollEd"
              className="mx-auto mb-8"
              style={{ width: '280px', height: 'auto' }}
            />
          </div>
          <p className="text-gray-400 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen">
      {/* Offline indicator - shows when user has no network connection */}
      <OfflineBanner />

      <BrowserRouter basename={routerBasename}>
        <Routes>
          <Route
            path="/"
            element={
              // 🆕 Story 1.5.9: Simplified routing using centralized shouldShowOnboarding
              !isAuthenticated ? (
                // Show auth modal for unauthenticated users
                <>
                  <AuthModal isOpen={true} onClose={() => {}} />
                </>
              ) : shouldShowOnboarding ? (
                // User needs to complete onboarding
                <Onboarding />
              ) : (
                // User completed onboarding - show main app
                <MainLayout
                  currentUser={currentUser}
                  initialInstance={
                    selectedInstance || {
                      id: "11111111-1111-1111-1111-111111111111",
                      name: "The Adventures of Arka",
                      description:
                        "Optimized test story for ScrollEd processing",
                    }
                  }
                  onResetOnboarding={resetOnboarding}
                />
              )
            }
          />
          <Route
            path="/profile/:entityId"
            element={<ProfilePage currentUser={currentUser} />}
          />
          <Route
            path="/reels"
            element={<ReelsPage currentUser={currentUser} />}
          />
          <Route path="/phase2-demo" element={<Phase2Demo />} />
          <Route path="/carousel-test" element={<CarouselTestPage />} />
          <Route path="/auth-callback" element={<AuthCallbackHandler />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<FallbackRoute />} />
        </Routes>
      </BrowserRouter>
      <CookieConsent />

      <Toaster position="top-center" />
    </div>
  );
}

// Root App Component with AuthProvider, ProcessingProvider, and ChapterProvider
function App() {
  return (
    <AuthProvider>
      <ProcessingProvider>
        <ChapterProvider>
          <AppContent />
        </ChapterProvider>
      </ProcessingProvider>
    </AuthProvider>
  );
}

export default App;

